import React, { CSSProperties } from "react";
import styled, { css } from "styled-components";

import { IThemeProps } from "@/styles";
import Svg from "@/styles/utils/Svg";

export interface ThumbsToggleProps {
  count: number;
  showCount: boolean;
  label: string;
  highlighted: boolean;
  disabled: boolean;
  iconContents: string;
  iconStyles?: CSSProperties;
  onClick: () => void;
}

const Thumbs: React.FC<ThumbsToggleProps> = (
  props: ThumbsToggleProps
): React.JSX.Element => {
  const {
    label,
    count,
    onClick,
    iconContents,
    iconStyles,
    highlighted,
    disabled,
    showCount,
  } = props;
  return (
    <Wrapper $highlighted={highlighted} $disabled={disabled}>
      <Toggle
        $highlighted={highlighted}
        href="#"
        onClick={(e: React.SyntheticEvent) => {
          e.preventDefault();
          onClick();
        }}
        onTouchEnd={(e: React.SyntheticEvent) => {
          e.preventDefault();
          onClick();
        }}
      >
        <HoverArea $highlighted={highlighted} $disabled={disabled}>
          <Label>{label}</Label>
          <Icon style={iconStyles}>
            <Svg contents={iconContents} />
          </Icon>
        </HoverArea>
        {showCount && <Count data-qa="thumbs:count">{count}</Count>}
      </Toggle>
    </Wrapper>
  );
};

type ToggleProps = IThemeProps<{
  $highlighted: boolean;
}>;
const Toggle = styled.a<ToggleProps>`
  display: flex;
  align-items: center;
  font-size: 20px;
  min-width: 50%;
  justify-content: center;
  flex-direction: column;
  pointer-events: ${(props: ToggleProps) => (props.$highlighted ? "none" : "")};
`;

const Icon = styled.span`
  position: relative;
  pointer-events: none;
  bottom: 0;
  left: 0;
  svg {
    width: 36px;
    height: 36px;
  }
  @media ${(props: IThemeProps) => props.theme.breakpoints.mobile} {
    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const Label = styled.span`
  font-family: ${(props: IThemeProps) => props.theme.globals.fontFamilyExtra};
  font-size: ${(props: IThemeProps) => props.theme.typography.h800};
  font-weight: 800;
  text-transform: uppercase;
  margin-right: 21px;

  @media ${(props: IThemeProps) => props.theme.breakpoints.mobile} {
    font-size: ${(props: IThemeProps) => props.theme.typography.h600};
    margin-right: 10px;
  }
`;

const Count = styled.div`
  font-size: ${(props: IThemeProps) => props.theme.typography.h800};
  color: ${(props: IThemeProps) => props.theme.colors.thumbsCount};
  font-weight: 600;

  @media ${(props: IThemeProps) => props.theme.breakpoints.mobile} {
    font-size: ${(props: IThemeProps) => props.theme.typography.h600};
  }
`;

type HoverAreaProps = IThemeProps<{
  $highlighted: boolean;
  $disabled: boolean;
}>;
const HoverArea = styled.div<HoverAreaProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;

  ${(props: HoverAreaProps) => {
    let color = props.$highlighted
      ? props.theme.colors.thumbsButtonHover
      : props.$disabled
        ? props.theme.colors.thumbsButtonDisabled
        : props.theme.colors.thumbsButton;

    return `
            color: ${color};
            svg {
                fill: ${color};
                stroke: ${color};
                ${props.$disabled && `path:last-child { fill: inherit; }`}
            }
        `;
  }}

  ${(props: HoverAreaProps) =>
    !props.theme.globals.isTouch &&
    `&:hover {
            color: ${props.theme.colors.thumbsButtonHover};
            svg {
                fill: ${props.theme.colors.thumbsButtonHover};
                stroke: ${props.theme.colors.thumbsButtonHover};
            }
        }`}

    @media ${(props: HoverAreaProps) => props.theme.breakpoints.mobile} {
    height: auto;
  }
`;

const WrapperDarkLayer = css`
  will-change: transform;
  transition: all 250ms;

  ${(props: WrapperProps) =>
    !props.theme.globals.isTouch &&
    !props.$highlighted &&
    `&:hover {
            transform: scale(1.02) translateZ(0);
            ${HoverArea} {
                color: ${props.theme.colors.thumbsButtonHover};
                svg {
                    fill: ${props.theme.colors.thumbsButtonHover};
                    stroke: ${props.theme.colors.thumbsButtonHover};
                }
            }
        }`}
`;

const WrapperShadowLayer = css`
  box-shadow: ${(props: WrapperProps) => props.theme.shadows.s300};
`;

const WrapperBackgroundHover = css`
  background-color: ${({ theme: { colors }, $highlighted }: WrapperProps) =>
    $highlighted
      ? colors.thumbsButtonBackgroundHover
      : colors.thumbsButtonBackground};
  &:hover {
    background-color: ${(props: WrapperProps) =>
      props.theme.colors.thumbsButtonBackgroundHover};
  }
`;

type WrapperProps = IThemeProps<{
  $highlighted: boolean;
  $disabled: boolean;
}>;
const Wrapper = styled.div<WrapperProps>`
  padding: ${(props: WrapperProps) => props.theme.spacing.desktop.s300}px;
  border-radius: ${(props: WrapperProps) =>
    props.theme.borders.thumbsButtonBorderRadius};
  background-color: ${(props: WrapperProps) =>
    props.theme.colors.thumbsButtonBackground};
  ${(props: WrapperProps) =>
    props.theme.thesis.hasBoxShadow && WrapperShadowLayer}
  ${(props: WrapperProps) =>
    props.theme.thesis.hasThumbsHoverEffect && WrapperDarkLayer}
  ${(props: WrapperProps) =>
    props.theme.colors.thumbsButtonBackgroundHover && WrapperBackgroundHover}
`;

export default Thumbs;
export { Thumbs };
